import { useCallback, useEffect, useMemo, useState } from 'react'
import { AuthSessionProvider, useAuthSession } from './auth/AuthSessionContext'
import { SignInScreen } from './auth/SignInScreen'
import { CoachWorkspace } from './components/CoachWorkspaceV2'
import { CoreServiceConsent } from './components/CoreServiceConsent'
import { DeviceCenter } from './components/DeviceCenter'
import { OnboardingWorkspace } from './components/OnboardingWorkspace'
import { PartnerInviteClaim } from './components/PartnerInviteClaim'
import { PartnerWorkspace } from './components/PartnerWorkspace'
import { PrivacyCenter } from './components/PrivacyCenter'
import { UserJourneyWorkspace as UserWorkspace } from './components/UserJourneyWorkspace'
import { WorkspaceToggle } from './components/WorkspaceToggle'
import { createPmmRepository } from './data/repository'
import { hasOwnerCoachCapability, hasPartnerAccess, hasUserAccess } from './domain/auth'
import { isConsentGranted } from './domain/consent'
import type { WorkspaceMode } from './domain/types'
import { disablePmmPush } from './notifications/pushClient'
import './onboarding.css'
import './intelligence.css'
import './partner-sovereign.css'

export function PmmApp() {
  const { session, loading, mode: authMode, signOut, reauthenticate, claimOwner } = useAuthSession()
  const [mode, setMode] = useState<WorkspaceMode>('user')
  const [surface, setSurface] = useState<'workspace' | 'privacy' | 'device'>('workspace')
  const [coreConsent, setCoreConsent] = useState<boolean | null>(null)
  const [accessError, setAccessError] = useState('')
  const [onboardingComplete, setOnboardingComplete] = useState<boolean | null>(null)
  const partnerInviteToken = new URLSearchParams(window.location.search).get('partner-invite')?.trim() ?? ''
  const canCoach = hasOwnerCoachCapability(session)
  const repository = useMemo(() => session ? createPmmRepository(session) : null, [session])

  useEffect(() => {
    if (!canCoach && mode === 'coach') setMode('user')
  }, [canCoach, mode])

  useEffect(() => {
    let active = true
    if (!repository || !hasUserAccess(session)) {
      setCoreConsent(null)
      return () => { active = false }
    }
    setCoreConsent(null)
    void repository.getPrivacySnapshot().then((snapshot) => {
      if (active) setCoreConsent(isConsentGranted(snapshot.consent, 'core_service'))
    }).catch(() => {
      if (active) setCoreConsent(false)
    })
    return () => { active = false }
  }, [repository, session])

  useEffect(() => {
    let active = true
    if (!repository || coreConsent !== true) {
      setOnboardingComplete(null)
      return () => { active = false }
    }
    void repository.getOnboardingSession().then((onboarding) => {
      if (active) setOnboardingComplete(onboarding.state === 'submitted')
    }).catch(() => {
      if (active) setOnboardingComplete(false)
    })
    return () => { active = false }
  }, [coreConsent, repository])

  const completeOnboarding = useCallback(() => setOnboardingComplete(true), [])

  const restartOnboardingJourney = useCallback(() => {
    setOnboardingComplete(false)
    setSurface('workspace')
    setMode('user')
  }, [])

  async function quickSignOut() {
    if (repository) await disablePmmPush(repository).catch(() => undefined)
    await repository?.clearSensitiveDeviceState()
    await signOut()
  }

  if (loading) return <main className="auth-loading"><span /><p>Securing your private workspace...</p></main>
  if (!session) return <SignInScreen />
  if (hasPartnerAccess(session)) return <PartnerWorkspace onSignOut={signOut} />
  if (!hasUserAccess(session) || !repository) {
    if (partnerInviteToken) return <PartnerInviteClaim token={partnerInviteToken} />
    return (
      <main className="access-denied">
        <p className="eyebrow">Access paused</p>
        <h1>This account does not have active PMM user permission.</h1>
        <p>Role changes are issued by the server and cannot be repaired from the browser.</p>
        {authMode === 'firebase' && (
          <button type="button" onClick={() => {
            setAccessError('')
            void claimOwner().catch(() => setAccessError('This verified account is not on the private owner allowlist.'))
          }}>Activate allowlisted owner account</button>
        )}
        {accessError && <p className="auth-error" role="alert">{accessError}</p>}
        <button type="button" onClick={() => void signOut()}>Sign out safely</button>
      </main>
    )
  }
  if (coreConsent === null) return <main className="auth-loading"><span /><p>Checking your privacy choices...</p></main>
  if (!coreConsent) {
    return <CoreServiceConsent
      onAccept={async () => {
        const state = await repository.acceptCoreService()
        setCoreConsent(isConsentGranted(state, 'core_service'))
      }}
      onSignOut={quickSignOut}
    />
  }

  const initials = session.displayName.split(/\s+/).map((part) => part[0]).join('').slice(0, 2).toUpperCase() || 'PM'

  return (
    <div className={`app-shell mode-${mode}`}>
      <header className="app-header">
        <button className="brand brand-button" type="button" onClick={() => { setSurface('workspace'); setMode('user') }} aria-label="Primal Masculine Man home">
          <span className="brand-mark">PM</span>
          <span><strong>Primal</strong><small>Masculine Man</small></span>
        </button>
        {canCoach && surface === 'workspace'
          ? <WorkspaceToggle mode={mode} onChange={setMode} />
          : <div className="user-access-pill"><span />User workspace</div>}
        <div className="header-meta">
          <button type="button" className={surface === 'device' ? 'privacy-nav is-active' : 'privacy-nav'} onClick={() => setSurface(surface === 'device' ? 'workspace' : 'device')}>Install & reminders</button>
          <button type="button" className={surface === 'privacy' ? 'privacy-nav is-active' : 'privacy-nav'} onClick={() => setSurface(surface === 'privacy' ? 'workspace' : 'privacy')}>Privacy & data</button>
          <span className="session-badge">{authMode === 'prototype' ? 'Protected prototype' : 'Authenticated'}</span>
          <span className="avatar" title={session.email}>{initials}</span>
        </div>
      </header>

      <div className="mode-banner" role="status">
        <span className="mode-dot" />
        {surface === 'device'
          ? 'Device centre - install, neutral reminders and encrypted offline access'
          : surface === 'privacy'
            ? 'Privacy centre - consent, device protection and data rights'
          : mode === 'user'
            ? 'User workspace - The Primal Foundation and Your Primal Path'
            : 'Coach workspace - server-authorised private review'}
        <small>{repository.mode === 'prototype' ? 'Memory-only data' : 'Firestore rules enforced'}</small>
      </div>

      {surface === 'workspace' && mode === 'user' && onboardingComplete === null
        ? <main className="auth-loading"><span /><p>Checking your starting baseline...</p></main>
        : surface === 'privacy'
          ? <PrivacyCenter repository={repository} session={session} onBack={() => setSurface('workspace')} onQuickSignOut={quickSignOut} onReauthenticate={reauthenticate} />
          : surface === 'device'
            ? <DeviceCenter repository={repository} onBack={() => setSurface('workspace')} />
            : mode === 'user'
              ? onboardingComplete
                ? <UserWorkspace repository={repository} />
                : <OnboardingWorkspace repository={repository} onComplete={completeOnboarding} />
              : canCoach
                ? <CoachWorkspace repository={repository} onOnboardingRestarted={restartOnboardingJourney} />
                : null}

      <footer className="app-footer">
        <span>Primal Masculine Man - Loop 10 owner + partner canary</span>
        <span>The Primal Foundation · Your Primal Path · Coach-gated review</span>
      </footer>
    </div>
  )
}

export default function App() {
  return <AuthSessionProvider><PmmApp /></AuthSessionProvider>
}
